import { NgTemplateOutlet } from '@angular/common';
import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	BadgeComponent,
	ButtonIconComponent,
	CollapseComponent,
	CopyTextComponent,
	DividerComponent,
	EmptyStateComponent,
	IconComponent,
	IMenu,
	InfoGroupComponent,
	PopoverComponent,
	TagComponent,
	TileComponent,
} from '@tecalisdev/infinity';
import { formatDate } from '../../../../core/utils/utils';
import { ExpirationDatePipe } from '../../../../core/pipes/expiration-date.pipe';
import { SafeDatePipe } from '../../../../core/pipes/safe-date.pipe';
import { OPERATION_STATUS } from '../../../../operations/models/operations.models';
import { MenuItemAction } from '../../../../shared/models/master.models';
import { MasterService } from '../../../../shared/services/master.service';
import { DeveloperCurlButtonComponent } from '../../../../shared/components/developer-curl-button/developer-curl-button.component';
import {
	IOperationOcrDetail,
	IProcessDetailResponse,
	IProcessFieldHistoryItem,
} from '../../../models/processes.models';
import { ProcessHelperService } from '../../../services/process-helper.service';
import { ocrDetailCaptureMethodLabelKey } from '../../../../workflows/utils/workflow-ocr.util';
@Component({
	selector: 'app-operation-detail-ocr',
	imports: [
		NgTemplateOutlet,
		TranslatePipe,
		SafeDatePipe,
		ExpirationDatePipe,
		CollapseComponent,
		TileComponent,
		EmptyStateComponent,
		ButtonIconComponent,
		InfoGroupComponent,
		IconComponent,
		TagComponent,
		BadgeComponent,
		CopyTextComponent,
		DividerComponent,
		PopoverComponent,
		DeveloperCurlButtonComponent,
	],
	templateUrl: './operation-detail-ocr.component.html',
	styleUrl: './operation-detail-ocr.component.scss',
})
export class OperationDetailOcrComponent {
	@Input() operation?: IOperationOcrDetail;
	@Input() processUuid?: string;
	@Input() processDetail?: IProcessDetailResponse;
	@Input() validation?: string;
	@Input() expanded = false;
	@Input() loading = false;
	@Input() contentReady = true;
	@Input() fieldHistoryByKey: Map<string, IProcessFieldHistoryItem> = new Map();
	/** NIF-1047: CTA sync identidad ← OCR (solo si OCR ≠ identidad). */
	@Input() showUpdateIdentityFromOcr = false;
	@Input() updateIdentityFromOcrLoading = false;
	@Output() toggleCollapse = new EventEmitter<boolean>();
	@Output() identityMenuAction = new EventEmitter<{ action: IMenu; optionsSelected?: string[] }>();
	@Output() updateIdentityFromOcr = new EventEmitter<void>();

	public masterService = inject(MasterService);
	public processHelper = inject(ProcessHelperService);
	private trans = inject(TranslateService);

	public OPERATION_STATUS = OPERATION_STATUS;
	public menuItemAction = MenuItemAction;
	/** Método real OCR (`capture_method`) con fallback a `mode` (NIF-921). */
	public readonly ocrDetailCaptureMethodLabelKey = ocrDetailCaptureMethodLabelKey;

	public get operationCurlPath(): string {
		return this.processUuid && this.operation?.uuid
			? `/process/${this.processUuid}/operations/${this.operation.uuid}`
			: '';
	}

	public ocrFieldHistory(field: string): IProcessFieldHistoryItem | null {
		return this.fieldHistoryByKey?.get(`operations_data|${field}`) ?? null;
	}

	public formatFieldHistoryPreviousValue(change: IProcessFieldHistoryItem): string {
		const raw = change.previous_value;
		if (raw == null || raw === '') {
			return this.trans.instant('GENERAL.NOT_AVAILABLE');
		}
		const asString = String(raw).trim();
		if (this.isFieldHistoryDateField(change.field) || this.looksLikeIsoDateValue(asString)) {
			return formatDate(asString, 'dd/MM/yyyy');
		}
		return asString;
	}

	public formatSecondsToMinutes(seconds: number): string {
		if (seconds === null || seconds === undefined) {
			return '';
		}
		const total = Math.max(0, Math.trunc(Number(seconds) || 0));
		const minutes = Math.floor(total / 60);
		const remainingSeconds = total % 60;
		return `${minutes}m ${remainingSeconds}s`;
	}

	public onIdentityMenu(action: IMenu, optionsSelected?: string[]): void {
		this.identityMenuAction.emit({ action, optionsSelected });
	}

	public onUpdateIdentityFromOcr(): void {
		if (this.updateIdentityFromOcrLoading) {
			return;
		}
		this.updateIdentityFromOcr.emit();
	}

	public onUpdateIdentityFromOcrClick(event: Event): void {
		event.stopPropagation();
		this.onUpdateIdentityFromOcr();
	}

	private isFieldHistoryDateField(field: string): boolean {
		return field === 'birthdate' || field === 'birth_date' || field === 'expiration_date';
	}

	private looksLikeIsoDateValue(value: string): boolean {
		return /^\d{4}-\d{2}-\d{2}(T|\s|$)/.test(value);
	}
}
